// Utility function to calculate lease centroids and aggregate data
// Now groups by lease + county to create separate markers for leases spanning multiple counties
export const calculateLeaseCentroids = (wellsData) => {
  const leases = new Map(); // Key: "DISTRICT-LEASENUMBER-COUNTY"

  if (!wellsData || wellsData.length === 0) {
    return [];
  }

  // Helper function to generate consistent lease keys
  const generateLeaseKey = (district, leaseNumber, leaseName) => {
    const dist = district || '00';
    const num = leaseNumber ? (typeof leaseNumber === 'string' ? leaseNumber.trim() : String(leaseNumber)) : null;
    const name = leaseName ? (typeof leaseName === 'string' ? leaseName.trim() : String(leaseName)) : null;
    
    if (num && num !== 'Unknown Lease' && num !== '') {
      // Normal case: District-LeaseNumber
      return `${String(dist).padStart(2, '0')}-${num}`.toUpperCase();
    } else if (name && name !== 'Unknown Lease' && name !== '') {
      // Missing lease number but have lease name: District-Unknown Lease-LeaseName
      return `${String(dist).padStart(2, '0')}-Unknown Lease-${name.toUpperCase()}`;
    } else {
      // Both missing: District-Unknown Lease
      return `${String(dist).padStart(2, '0')}-Unknown Lease`;
    }
  };

  wellsData.forEach(well => {
    const district = well['District'] || well.district || '00';
    const leaseNumber = well['Lease Number'] || well.lease_number || null;
    const leaseName = well['Lease Name'] || well.lease_name || null;
    const county = (well['County'] || well.county || 'Unknown County').toString().trim();
    const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
    const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);

    // Only filter out if coordinates are invalid (allow missing lease numbers if coordinates exist)
    if (isNaN(lat) || isNaN(lng)) return;

    const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
    // Create composite key: leaseKey + county for separate markers per county
    const countyLeaseKey = `${leaseKey}-${county}`;

    if (!leases.has(countyLeaseKey)) {
      leases.set(countyLeaseKey, {
        leaseKey, // Original lease key (for matching with flags and aggregated data)
        countyLeaseKey, // Unique key for this county-lease combination
        county, // County name
        leaseNumber: leaseNumber || null,
        district,
        leaseName: well['Lease Name'] || well.lease_name || 'Unknown Lease',
        wells: [],
        coordinates: [],
      });
    }

    const lease = leases.get(countyLeaseKey);
    lease.wells.push(well);
    lease.coordinates.push([lat, lng]);
  });

  // Calculate centroid for each lease-county combination (for any lease with 1+ wells in that county)
  const leaseCentroids = Array.from(leases.entries())
    .filter(([, lease]) => lease.wells.length >= 1) // Include leases with 1+ wells
    .map(([, lease]) => {
      const coords = lease.coordinates;
      const avgLat = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
      const avgLng = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;

      return {
        leaseKey: lease.leaseKey, // Original lease key (for matching with flags and aggregated data)
        countyLeaseKey: lease.countyLeaseKey, // Unique key for map marker identification
        county: lease.county, // County name
        leaseNumber: lease.leaseNumber,
        district: lease.district,
        leaseName: lease.leaseName,
        centroid: [avgLat, avgLng],
        wells: lease.wells, // Only wells from this county
        wellCount: lease.wells.length, // Count of wells in this county
      };
    });

  return leaseCentroids;
};

// Utility function to create lines connecting lease centroids to associated wells
export const createLeaseWellConnections = (lease) => {
  const connections = [];
  
  if (!lease || !lease.wells || !lease.centroid) {
    return connections;
  }

  lease.wells.forEach(well => {
    const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
    const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
    if (!isNaN(lat) && !isNaN(lng)) {
      connections.push({
        geometry: {
          coordinates: [
            [lease.centroid[0], lease.centroid[1]], // [lat, lng] for Leaflet
            [lat, lng]
          ]
        },
        properties: {
          leaseKey: lease.leaseKey,
          apiNumber: well['API Number'] || well.api_number,
          wellName: well['Well Name'] || well.well_name,
        }
      });
    }
  });
  
  return connections;
};

